import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { synchronizeSession, watchSession, type BrowserSessionState } from "@/shared/services/browserSession.service";

export type WarningLevel = "first_warning" | "second_warning" | null;
const HEARTBEAT_MS = 30_000;
const ACTIVITY_THROTTLE_MS = 30_000;

// One server-side login family is shared across tabs; other devices have a
// different authenticated Pusher channel. Background heartbeats never reset idle.
export function useBrowserSession(identity: string | undefined, logout: () => unknown) {
  const [session, setSession] = useState<BrowserSessionState | null>(null);
  const [owner, setOwner] = useState(identity);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isHandlingActivity, setIsHandlingActivity] = useState(false);
  const syncRef = useRef<(activity: boolean) => Promise<void>>(async () => {});
  const logoutRef = useRef(logout);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    if (!identity) return;
    let stopped = false;
    let ended = false;
    let current: BrowserSessionState | null = null;
    let stopWatching: (() => void) | undefined;
    let pending: Promise<void> | null = null;
    let queuedActivity = false;
    let queuedRead = false;
    let lastInteractionSent = -Infinity;
    let trailingInteraction: ReturnType<typeof setTimeout> | undefined;
    let warningEnd: number | null = null;
    let absoluteEnd: number | null = null;

    const endSession = () => {
      if (stopped || ended) return;
      ended = true;
      setSession(null);
      setCountdown(null);
      void logoutRef.current();
    };

    const acceptState = (next: BrowserSessionState | null, requestStarted: number) => {
      if (stopped) return;
      if (!next) return;
      // Legacy or impersonation token, no managed session.
      if (next.revoked) {
        endSession();
        return;
      }
      if (next.channel !== current?.channel) {
        stopWatching?.();
        stopWatching = watchSession(next.channel, () => {
          void sync(false);
        });
      }
      current = next;
      const now = performance.now();
      warningEnd = next.warning_deadline === null ? null : requestStarted + (next.warning_deadline - next.server_time) * 1000;
      absoluteEnd = requestStarted + (next.absolute_deadline - next.server_time) * 1000;

      setOwner(identity);
      setSession(next);
      setCountdown(warningEnd === null ? null : Math.max(0, Math.ceil((warningEnd - now) / 1000)));
    };

    const sync = (activity: boolean): Promise<void> => {
      if (stopped || ended) return Promise.resolve();
      if (activity) queuedActivity = true;
      if (pending) {
        queuedRead = true;
        return pending;
      }
      pending = (async () => {
        do {
          const sendActivity = queuedActivity;
          queuedActivity = false;
          queuedRead = false;
          const requestStarted = performance.now();
          try {
            const next = await synchronizeSession(sendActivity);
            acceptState(next, requestStarted);
            if (sendActivity) lastInteractionSent = performance.now();
          }
          catch (error) {
            const status = (error as { response?: { status?: number } }).response?.status;
            if (status === 401) endSession();
            // A network error must not close a warning or extend a known deadline.
            if (absoluteEnd !== null && performance.now() >= absoluteEnd) endSession();
            if (warningEnd !== null && performance.now() >= warningEnd) endSession();
            queuedActivity = false;
            queuedRead = false;
          }
        } while ((queuedActivity || queuedRead) && !stopped && !ended);
      })().finally(() => {
        pending = null;
      });
      return pending;
    };
    syncRef.current = sync;

    const interact = (event: Event) => {
      if (!event.isTrusted || document.visibilityState === "hidden") return;
      const remaining = ACTIVITY_THROTTLE_MS - (performance.now() - lastInteractionSent);
      if (current?.warning_deadline || remaining <= 0) {
        clearTimeout(trailingInteraction);
        trailingInteraction = undefined;
        void sync(true);
      }
      else if (trailingInteraction === undefined) {
        trailingInteraction = setTimeout(() => {
          trailingInteraction = undefined;
          void sync(true);
        }, remaining);
      }
    };
    const resume = () => {
      if (document.visibilityState !== "hidden") void sync(true);
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((name) => window.addEventListener(name, interact, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", resume);

    // Opening/reopening a tab is an interaction, but the server checks expiry first.
    void sync(true);
    const heartbeat = setInterval(() => {
      void sync(false);
    }, HEARTBEAT_MS);
    const clock = setInterval(() => {
      if (ended) return;
      const now = performance.now();
      if (warningEnd !== null) setCountdown(Math.max(0, Math.ceil((warningEnd - now) / 1000)));
      if ((warningEnd !== null && now >= warningEnd) || (absoluteEnd !== null && now >= absoluteEnd)) {
        // Re-fetch in case another tab cancelled this warning and its push was lost.
        // This endpoint never records activity, and cannot revive an expired family.
        void sync(false);
      }
    }, 1000);

    return () => {
      stopped = true;
      stopWatching?.();
      clearInterval(heartbeat);
      clearTimeout(trailingInteraction);
      clearInterval(clock);
      events.forEach((name) => window.removeEventListener(name, interact, { capture: true }));
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", resume);
    };
  }, [identity]);

  const handleCloseDialog = useCallback(async () => {
    setIsHandlingActivity(true);
    try {
      await syncRef.current(true);
    }
    finally {
      setIsHandlingActivity(false);
    }
  }, []);

  return {
    showInactivityDialog: Boolean(identity && owner === identity && session?.warning_deadline),
    inactivityMessage: "Vous êtes inactif depuis une heure. Confirmez votre présence pour rester connecté.",
    warningLevel: (session?.warning_deadline ? "second_warning" : null) as WarningLevel,
    isHandlingActivity,
    countdown,
    handleCloseDialog,
  };
}
